import { useContext, useCallback, useMemo } from "react";
import { VersionedDataContext } from "../../../contexts/versioned_data/context";
import { LockForecastButtonComponent } from "./component";
import { useApi } from "../../../contexts/app/provider";
import { useRefetchCustomerSettings } from "../../../contexts/app/provider";
import { useCustomer } from "../../../contexts/app/provider";
import { SysMessagesContext } from "../../../contexts/sys_messages/context";
import { SysMessageLevel } from "../../../contexts/sys_messages/type";
import {
  useCopyForecastToDefaultSettingsMutation,
  useUnlockForecastFromDefaultSettingsMutation,
} from "../../../services/api/__generated__/backend_gateway-types";

export const LockForecastButtonContainer = () => {
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const { apiService } = useApi();
  const { refetchCustomerSettings } = useRefetchCustomerSettings();
  const { customerSettings } = useCustomer();
  const { addSystemMessage } = useContext(SysMessagesContext);

  // Get the run to use (mainRun or lastSuccessfulRun as fallback)
  const runToUse = mainRun || lastSuccessfulRun;

  // Check if the current run is the one that locked the forecast
  const isLocked = useMemo(() => {
    if (!runToUse || !customerSettings) {
      return false;
    }

    const forecastLockedToRunId = customerSettings.forecastLockedToRunId;
    if (!forecastLockedToRunId) {
      return false;
    }

    // Check if the current run's settings match the locked run settings
    return JSON.stringify(runToUse.runSettings) === forecastLockedToRunId;
  }, [runToUse, customerSettings]);

  const [copyForecastToDefaultSettings, { loading: lockLoading }] =
    useCopyForecastToDefaultSettingsMutation({
      client: apiService.getClient(),
      onCompleted: (data) => {
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: `Forecast locked successfully`,
        });
        refetchCustomerSettings();
      },
      onError: (error) => {
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: `Failed to lock forecast: ${error.message}`,
        });
      },
    });

  const [unlockForecastFromDefaultSettings, { loading: unlockLoading }] =
    useUnlockForecastFromDefaultSettingsMutation({
      client: apiService.getClient(),
      onCompleted: (data) => {
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: `Forecast unlocked successfully`,
        });
        refetchCustomerSettings();
      },
      onError: (error) => {
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: `Failed to unlock forecast: ${error.message}`,
        });
      },
    });

  const handleToggleLock = useCallback(async () => {
    if (isLocked) {
      // If already locked, unlock it
      try {
        await unlockForecastFromDefaultSettings();
      } catch (error) {
        // Error handling is done in the mutation's onError callback
        console.error("Error unlocking forecast:", error);
      }
    } else {
      // If not locked, lock it
      if (!runToUse) {
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message: "No run selected to copy forecast from",
        });
        return;
      }

      try {
        await copyForecastToDefaultSettings({
          variables: {
            sourceInputId: runToUse.runSettings.inputId,
            sourceSettingsNameAndVersion:
              runToUse.runSettings.settingsNameAndVersion,
            sourceRunId: JSON.stringify(runToUse.runSettings),
          },
        });
      } catch (error) {
        // Error handling is done in the mutation's onError callback
        console.error("Error copying forecast to default settings:", error);
      }
    }
  }, [
    isLocked,
    runToUse,
    copyForecastToDefaultSettings,
    unlockForecastFromDefaultSettings,
    addSystemMessage,
  ]);

  return (
    <LockForecastButtonComponent
      mainRun={runToUse}
      isLocked={isLocked}
      onToggleLock={handleToggleLock}
      loading={lockLoading || unlockLoading}
    />
  );
};
